import { supabase } from '../lib/supabase';
import { handleError } from '../utils/themeUtils';

// Medications Service
export const medicationService = {
  // Get all medications with their dose times
  async getAll() {
    const { data, error } = await supabase
      .from('medications')
      .select(`
        *,
        medication_doses (
          dose_number,
          scheduled_time
        )
      `)
      .order('time', { ascending: true })
      .order('name', { ascending: true });

    if (error) {
      handleError(error, 'fetching medications');
    }
    
    // Transform to include doses array sorted by dose_number
    return (data || []).map(med => ({
      ...med,
      doses: (med.medication_doses || [])
        .sort((a, b) => a.dose_number - b.dose_number)
        .map(d => ({
          dose_number: d.dose_number,
          scheduled_time: d.scheduled_time
        }))
    }));
  },

  // Get medication by ID
  async getById(id) {
    const { data, error } = await supabase
      .from('medications')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      handleError(error, 'fetching medication');
    }
    return data;
  },

  // Create new medication
  async create(medication) {
    const { data, error } = await supabase
      .from('medications')
      .insert([medication])
      .select()
      .single();

    if (error) {
      console.error('Error creating medication:', error);
      throw error;
    }
    return data;
  },

  // Update medication
  async update(id, updates) {
    const { data, error } = await supabase
      .from('medications')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating medication:', error);
      throw error;
    }
    return data;
  },

  // Delete medication
  async delete(id) {
    const { error } = await supabase
      .from('medications')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting medication:', error);
      throw error;
    }
  },
};

// Medication Doses Service
export const medicationDoseService = {
  // Get all doses for a medication
  async getByMedicationId(medicationId) {
    const { data, error } = await supabase
      .from('medication_doses')
      .select('*')
      .eq('medication_id', medicationId)
      .order('dose_number', { ascending: true });

    if (error) {
      console.error('Error fetching medication doses:', error);
      throw error;
    }
    return data || [];
  },

  // Create or update doses for a medication
  async upsertDoses(medicationId, doses) {
    // Delete existing doses for this medication
    await supabase
      .from('medication_doses')
      .delete()
      .eq('medication_id', medicationId);

    if (doses.length === 0) {
      return [];
    }

    // Insert new doses
    const doseRecords = doses.map((dose, index) => ({
      medication_id: medicationId,
      dose_number: index + 1,
      scheduled_time: dose,
    }));

    const { data, error } = await supabase
      .from('medication_doses')
      .insert(doseRecords)
      .select();

    if (error) {
      console.error('Error upserting medication doses:', error);
      throw error;
    }
    return data || [];
  },

  // Delete all doses for a medication
  async deleteByMedicationId(medicationId) {
    const { error } = await supabase
      .from('medication_doses')
      .delete()
      .eq('medication_id', medicationId);

    if (error) {
      console.error('Error deleting medication doses:', error);
      throw error;
    }
  },
};

// Medication Logs Service
export const medicationLogService = {
  // Get all medication logs
  async getAll() {
    const { data, error } = await supabase
      .from('medication_logs')
      .select(`
        *,
        medications (
          name,
          dosage
        )
      `)
      .order('taken_at', { ascending: false });

    if (error) {
      console.error('Error fetching medication logs:', error);
      throw error;
    }
    return data || [];
  },

  // Get logs for a specific medication
  async getByMedicationId(medicationId) {
    const { data, error } = await supabase
      .from('medication_logs')
      .select(`
        *,
        medications (
          name,
          dosage
        )
      `)
      .eq('medication_id', medicationId)
      .order('taken_at', { ascending: false });

    if (error) {
      console.error('Error fetching medication logs:', error);
      throw error;
    }
    return data || [];
  },

  // Get today's medication logs
  async getToday() {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('medication_logs')
      .select(`
        *,
        medications (
          name,
          dosage
        )
      `)
      .gte('taken_at', `${today}T00:00:00`)
      .lt('taken_at', `${today}T23:59:59`)
      .order('taken_at', { ascending: false });

    if (error) {
      handleError(error, 'fetching today\'s medication logs');
    }
    return data || [];
  },

  // Log medication taken
  async logTaken(medicationId, userName = null, doseNumber = null) {
    const { data, error } = await supabase
      .from('medication_logs')
      .insert([
        {
          medication_id: medicationId,
          user_name: userName,
          dose_number: doseNumber,
        },
      ])
      .select(`
        *,
        medications (
          name,
          dosage
        )
      `)
      .single();

    if (error) {
      handleError(error, 'logging medication taken');
    }
    return data;
  },

  // Delete today's medication logs (for testing)
  async deleteToday() {
    const today = new Date().toISOString().split('T')[0];
    const { error } = await supabase
      .from('medication_logs')
      .delete()
      .gte('taken_at', `${today}T00:00:00`)
      .lt('taken_at', `${today}T23:59:59`);

    if (error) {
      handleError(error, 'deleting today\'s medication logs');
      throw error;
    }
    return { success: true };
  },
};

// Real-time subscriptions
export const realtimeService = {
  // Subscribe to medication changes
  subscribeToMedications(callback) {
    return supabase
      .channel('medications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'medications',
        },
        (payload) => {
          callback(payload);
        }
      )
      .subscribe();
  },

  // Subscribe to medication log changes
  subscribeToMedicationLogs(callback) {
    return supabase
      .channel('medication-logs-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'medication_logs',
        },
        (payload) => {
          callback(payload);
        }
      )
      .subscribe();
  },

  // Unsubscribe from a channel
  unsubscribe(channel) {
    return supabase.removeChannel(channel);
  },
};

